import React from 'react';
import { Icon } from './icons.jsx';
import {
  AUDIT_ACTION_OPTIONS,
  ApiErrorState,
  EmptyState,
  SkeletonTable,
  USE_REAL_AUDIT_API,
  auditService,
  periodToDateFrom,
  resultUiToApi,
  severityUiToApi,
} from './services.jsx';
import { RetentionConfigModal } from './sistema-modals.jsx';
// Auditoria — log centralizado de todas as ações sensíveis
const { useState, useMemo, useEffect, useCallback } = React;

const SEVERITY_CONFIG = {
  info:       { label: 'Info',      bg: 'var(--info-bg)',     color: 'var(--info)'    },
  atencao:    { label: 'Atenção',   bg: 'var(--warning-bg)',  color: 'var(--warning)' },
  critico:    { label: 'Crítico',   bg: 'var(--danger-bg)',   color: 'var(--danger)'  },
  seguranca:  { label: 'Segurança', bg: '#f3e8ff',            color: '#7c3aed'        },
};

const RESULT_CONFIG = {
  success:  { label: 'Sucesso',   cls: 'success' },
  falha:    { label: 'Falha',     cls: 'danger'  },
  failed:   { label: 'Falha',     cls: 'danger'  },
  parcial:  { label: 'Parcial',   cls: 'warning' },
  pendente: { label: 'Pendente',  cls: 'neutral' },
  revertido:{ label: 'Revertido', cls: 'neutral' },
};

const AUDIT_V2_MOCK = [
  { id: 'evt_001', ts: 'Hoje, 14:22', actor: 'Ana Vargas',     actorType: 'Super admin', tenant: 'Garagem do Som',        tenantId: 29,  action: 'Tenant suspenso',              resource: 'Tenant #29',       result: 'success', severity: 'critico',   ip: '177.142.12.8',  reason: 'Inadimplência de 30 dias',           before: { status: 'ativo' },    after: { status: 'suspenso' } },
  { id: 'evt_002', ts: 'Hoje, 11:08', actor: 'Pedro Chaves',   actorType: 'Admin',       tenant: 'Dona Clara Doces',     tenantId: 4,   action: 'Login como lojista iniciado',   resource: 'Sessão #imp_88',   result: 'success', severity: 'seguranca', ip: '177.142.12.8',  reason: 'Diagnóstico de suporte solicitado',  before: null,                   after: { session: 'imp_88', duration: '30min' } },
  { id: 'evt_003', ts: 'Hoje, 09:47', actor: 'Beatriz Lima',   actorType: 'Suporte',     tenant: 'Moda Lis Atacado',     tenantId: 2,   action: 'Plano alterado',                resource: 'Plano Basic',     result: 'success', severity: 'atencao',   ip: '201.55.33.18',  reason: 'Upgrade solicitado pelo cliente',    before: { plan: 'basic' },      after: { plan: 'pro' } },
];

const SeverityBadge = ({ sev }) => {
  const c = SEVERITY_CONFIG[sev] || SEVERITY_CONFIG.info;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 11, fontWeight: 600 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
      {c.label}
    </span>
  );
};

const ResultBadge = ({ result }) => {
  const c = RESULT_CONFIG[result] || RESULT_CONFIG.success;
  return <span className={`badge ${c.cls}`}><span className="dot" />{c.label}</span>;
};

const AuditEventDrawerV2 = ({ event, open, onClose, relatedEvents = [] }) => {
  if (!open || !event) return null;
  const metadataJson = event.metadata && Object.keys(event.metadata).length
    ? JSON.stringify(event.metadata, null, 2)
    : null;

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer" style={{ width: 560 }}>
        <div className="drawer-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0 }}>{event.action}</h3>
            <div className="sub">{event.ts} · ID: <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>{event.id}</code></div>
          </div>
          <button className="icon-action" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="drawer-body">
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <SeverityBadge sev={event.severity} />
            <ResultBadge result={event.result} />
          </div>

          <div className="drawer-section">
            <h4>Identificação</h4>
            <div className="info-list">
              {[
                ['ID do evento', <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{event.id}</code>],
                ['Data/hora', event.ts],
                ['Ator', event.actor],
                ['E-mail do ator', event.actorEmail || '—'],
                ['IP', <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}>{event.ip}</code>],
              ].map(([l, v]) => (
                <div key={l} className="info-row"><span className="label">{l}</span><span className="value">{v}</span></div>
              ))}
            </div>
          </div>

          <div className="drawer-section">
            <h4>Evento</h4>
            <div className="info-list">
              {[
                ['Ação', event.action],
                ['Código', event.actionCode || '—'],
                ['Recurso', event.resource],
                ['Tenant afetado', event.tenant],
                ['Resultado', <ResultBadge result={event.result} />],
                ['Severidade', <SeverityBadge sev={event.severity} />],
                ['Motivo informado', event.reason || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Não informado</span>],
              ].map(([l, v]) => (
                <div key={l} className="info-row"><span className="label">{l}</span><span className="value">{v}</span></div>
              ))}
            </div>
          </div>

          {(event.before || event.after) && (
            <div className="drawer-section">
              <h4>Dados alterados</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {event.before && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Antes</div>
                    <pre style={{ margin: 0, padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', overflow: 'auto' }}>{JSON.stringify(event.before, null, 2)}</pre>
                  </div>
                )}
                {event.after && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Depois</div>
                    <pre style={{ margin: 0, padding: '10px 12px', background: 'var(--success-bg)', borderRadius: 8, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', overflow: 'auto' }}>{JSON.stringify(event.after, null, 2)}</pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {metadataJson && (
            <div className="drawer-section">
              <h4>Metadata</h4>
              <pre style={{ margin: 0, padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-secondary)', overflow: 'auto' }}>{metadataJson}</pre>
            </div>
          )}

          {relatedEvents.length > 0 && (
            <div className="drawer-section">
              <h4>Eventos relacionados</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                {relatedEvents.map(e => (
                  <div key={e.id} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon name="audit" size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{e.action}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.ts} · {e.actor}</div>
                    </div>
                    <SeverityBadge sev={e.severity} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="drawer-foot">
          <button className="btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </>
  );
};

const AuditoriaV2Page = ({ auditLog = [], tenantContext = null }) => {
  const useRealApi = USE_REAL_AUDIT_API !== false;
  const [search,       setSearch]       = useState('');
  const [severityF,    setSeverityF]    = useState('all');
  const [resultF,      setResultF]      = useState('all');
  const [actionF,      setActionF]      = useState('all');
  const [periodF,      setPeriodF]      = useState('7d');
  const [selected,     setSelected]     = useState(null);
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [activeChip,   setActiveChip]   = useState('all');
  const [tenantFilter, setTenantFilter] = useState(tenantContext || null);
  const [events,       setEvents]       = useState([]);
  const [loading,      setLoading]      = useState(useRealApi);
  const [error,        setError]        = useState(null);
  const [page,         setPage]         = useState(1);
  const [pageSize]                        = useState(20);
  const [totalCount,   setTotalCount]   = useState(0);

  const [retentionModal,  setRetentionModal]  = useState(false);
  const [retentionPolicy, setRetentionPolicy] = useState(null);

  const buildQueryParams = useCallback(() => {
    const params = {
      page,
      page_size: pageSize,
      date_from: periodToDateFrom(periodF),
    };
    if (search.trim()) params.search = search.trim();
    if (actionF !== 'all') params.action = actionF;
    if (severityF !== 'all') params.severity = severityUiToApi(severityF);
    if (resultF !== 'all') params.result = resultUiToApi(resultF);
    if (tenantFilter?.id) params.tenant_id = tenantFilter.id;
    if (activeChip === 'critico') params.severity = 'critical';
    if (activeChip === 'falha') params.result = 'failed';
    return params;
  }, [page, pageSize, periodF, search, actionF, severityF, resultF, tenantFilter, activeChip]);

  const fetchLogs = useCallback(async () => {
    if (!useRealApi) {
      const real = auditLog.map((a, i) => ({
        id: `real_${i}`,
        ts: a.when,
        actor: a.user,
        actorType: 'Super admin',
        tenant: a.storeName || '—',
        tenantId: a.storeId || null,
        action: a.action,
        resource: '—',
        result: a.kind === 'danger' ? 'falha' : 'success',
        severity: a.kind === 'danger' ? 'critico' : 'info',
        ip: a.ip || '—',
        reason: '—',
        before: null,
        after: null,
        metadata: {},
      }));
      setEvents([...AUDIT_V2_MOCK, ...real]);
      setTotalCount([...AUDIT_V2_MOCK, ...real].length);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await auditService.listLogs(buildQueryParams());
      setEvents(response.data || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      setError(err.message || 'Erro ao carregar auditoria.');
      setEvents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [useRealApi, auditLog, buildQueryParams]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [search, severityF, resultF, actionF, periodF, tenantFilter, activeChip]);

  const filtered = useMemo(() => {
    if (useRealApi) {
      return events.filter(e => {
        if (activeChip === 'seguranca' && e.severity !== 'seguranca') return false;
        if (activeChip === 'impersonacao' && !String(e.action).toLowerCase().includes('lojista')) return false;
        if (activeChip === 'repairs' && !String(e.action).toLowerCase().includes('repair')) return false;
        return true;
      });
    }
    return events.filter(e => {
      if (tenantFilter && e.tenantId !== null && e.tenantId !== tenantFilter.id) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![e.actor, e.tenant, e.action, e.resource, e.ip, e.reason].some(f => String(f).toLowerCase().includes(q))) return false;
      }
      if (severityF !== 'all' && e.severity !== severityF) return false;
      if (resultF   !== 'all' && e.result   !== resultF)   return false;
      if (actionF   !== 'all' && !String(e.action).toLowerCase().includes(actionF)) return false;
      if (activeChip === 'critico'      && e.severity !== 'critico')    return false;
      if (activeChip === 'seguranca'    && e.severity !== 'seguranca')   return false;
      if (activeChip === 'impersonacao' && !String(e.action).toLowerCase().includes('lojista')) return false;
      if (activeChip === 'repairs'      && !String(e.action).toLowerCase().includes('repair'))  return false;
      if (activeChip === 'falha'        && e.result   !== 'falha')       return false;
      return true;
    });
  }, [events, useRealApi, search, severityF, resultF, actionF, activeChip, tenantFilter]);

  const totalPages = Math.max(1, Math.ceil((useRealApi ? totalCount : filtered.length) / pageSize));

  const kpis = [
    { label: 'Eventos encontrados',   value: useRealApi ? totalCount : filtered.length, icon: 'audit' },
    { label: 'Ações críticas',        value: filtered.filter(e => e.severity === 'critico').length, icon: 'alert', danger: true },
    { label: 'Impersonações',         value: filtered.filter(e => String(e.action).toLowerCase().includes('lojista')).length, icon: 'impersonate' },
    { label: 'Repairs executados',    value: filtered.filter(e => String(e.action).toLowerCase().includes('repair')).length, icon: 'wrench' },
    { label: 'Falhas',                value: filtered.filter(e => e.result === 'falha' || e.result === 'failed').length, icon: 'x', danger: true },
    { label: 'Sem motivo informado',  value: filtered.filter(e => e.reason === '—' || !e.reason).length, icon: 'alert', warn: true },
  ];

  const handleExport = (fmt) => {
    const rows = filtered.map(e => `${e.ts},${e.actor},${e.actorType},${e.tenant},${e.action},${e.result},${e.severity}`);
    const blob = new Blob([['ts,actor,tipo,tenant,acao,resultado,severidade', ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `auditoria-tezus.${fmt}`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const openEvent = async (e) => {
    if (useRealApi && e?.id) {
      try {
        const detail = await auditService.getLog(e.id);
        setSelected(detail);
      } catch (err) {
        setSelected(e);
      }
    } else {
      setSelected(e);
    }
    setDrawerOpen(true);
  };

  const relatedEvents = selected && selected.tenantId
    ? filtered.filter(ev => ev.id !== selected.id && ev.tenantId === selected.tenantId).slice(0, 3)
    : [];

  const CHIP_FILTERS = [
    { id: 'all',          label: 'Todos' },
    { id: 'critico',      label: 'Críticos' },
    { id: 'seguranca',    label: 'Segurança' },
    { id: 'impersonacao', label: 'Impersonações' },
    { id: 'repairs',      label: 'Repairs' },
    { id: 'falha',        label: 'Falhas' },
  ];

  return (
    <div className="content wide" style={{ maxWidth: 1280 }}>
      <div className="page-header">
        <div>
          <h1>Auditoria</h1>
          <p className="sub">Histórico obrigatório de ações administrativas, suporte e segurança.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => handleExport('csv')}><Icon name="download" size={14} />Exportar lista (CSV)</button>
          <button className="btn" onClick={() => handleExport('json')}><Icon name="download" size={14} />Exportar lista (JSON)</button>
          {!useRealApi && (
            <button className="btn" onClick={() => setRetentionModal(true)}>
              <Icon name="settings" size={14} />Configurar retenção
              {retentionPolicy && <span style={{ fontSize: 10, background: 'var(--info-bg)', color: 'var(--info)', padding: '1px 6px', borderRadius: 8, fontWeight: 700, marginLeft: 4 }}>{retentionPolicy.label}</span>}
            </button>
          )}
          <button className="btn danger-outline" onClick={() => { setSeverityF('critico'); setActiveChip('critico'); }}>
            <Icon name="alert" size={14} />Ver críticos
          </button>
        </div>
      </div>

      {tenantFilter && (
        <div style={{ background: 'color-mix(in srgb, var(--info) 8%, var(--bg-elevated))', border: '1px solid color-mix(in srgb, var(--info) 30%, var(--border))', borderRadius: 12, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--info-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="audit" size={15} style={{ color: 'var(--info)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600, fontSize: 13 }}>Auditoria filtrada para <strong>{tenantFilter.name}</strong></div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Exibindo apenas eventos associados a este tenant</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--info-bg)', color: 'var(--info)', fontSize: 12, fontWeight: 600 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--info)' }} />
              Tenant: {tenantFilter.name}
            </span>
            <button className="btn sm" onClick={() => setTenantFilter(null)}>
              <Icon name="x" size={12} />Limpar filtro
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 24 }}>
        {kpis.map(k => (
          <div key={k.label} className={`kpi${k.danger ? ' accent-red' : ''}`}>
            <div className="label"><Icon name={k.icon} size={14} />{k.label}</div>
            <div className="value" style={{ color: k.warn ? 'var(--warning)' : undefined }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="table-wrap">
        <div className="table-toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="search" style={{ maxWidth: 280 }}>
            <Icon name="search" size={15} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por tenant, email, ação, IP…" />
          </div>
          <select className="select" value={periodF} onChange={e => setPeriodF(e.target.value)}>
            <option value="24h">Últimas 24h</option>
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="all">Tudo</option>
          </select>
          {useRealApi && (
            <select className="select" value={actionF} onChange={e => setActionF(e.target.value)}>
              {(AUDIT_ACTION_OPTIONS || [{ value: 'all', label: 'Todas as ações' }]).map(opt => (
                <option key={opt.value || 'all'} value={opt.value || 'all'}>{opt.label}</option>
              ))}
            </select>
          )}
          <select className="select" value={severityF} onChange={e => setSeverityF(e.target.value)}>
            <option value="all">Todas as severidades</option>
            <option value="info">Info</option>
            <option value="atencao">Atenção</option>
            <option value="critico">Crítico</option>
            <option value="seguranca">Segurança</option>
          </select>
          <select className="select" value={resultF} onChange={e => setResultF(e.target.value)}>
            <option value="all">Todos os resultados</option>
            <option value="success">Sucesso</option>
            <option value="falha">Falha</option>
            <option value="parcial">Parcial</option>
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            {useRealApi ? `${totalCount} eventos` : `${filtered.length} de ${events.length} eventos`}
          </span>
        </div>

        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap', background: 'var(--bg-elevated)' }}>
          {CHIP_FILTERS.map(f => (
            <button key={f.id} className={'filter-chip ' + (activeChip === f.id ? 'active' : '')} onClick={() => setActiveChip(f.id)}>
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: 32 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody><SkeletonTable rows={6} cols={10} /></tbody></table>
          </div>
        ) : error ? (
          <ApiErrorState error={error} onRetry={fetchLogs} />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Data/hora</th>
                <th>Ator</th>
                <th>Tenant</th>
                <th>Ação</th>
                <th>Recurso</th>
                <th>Resultado</th>
                <th>Severidade</th>
                <th>IP</th>
                <th>Motivo</th>
                <th style={{ textAlign: 'right' }}>Ver</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => openEvent(e)}>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>{e.ts}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--brand-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                        {String(e.actor).split(' ').map(n => n[0]).slice(0, 2).join('')}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{e.actor}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{e.actorType}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: e.tenant === '—' ? 'var(--text-muted)' : 'var(--text)' }}>{e.tenant}</td>
                  <td style={{ fontWeight: 500, fontSize: 13 }}>{e.action}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.resource}</td>
                  <td><ResultBadge result={e.result} /></td>
                  <td><SeverityBadge sev={e.severity} /></td>
                  <td style={{ fontSize: 12, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>{e.ip}</td>
                  <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: e.reason === '—' ? 'var(--danger)' : 'var(--text-secondary)' }}>
                    {e.reason === '—' ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name="alert" size={12} />Não informado</span> : e.reason}
                  </td>
                  <td onClick={ev => ev.stopPropagation()}>
                    <div className="row-actions">
                      <button className="icon-action" title="Ver detalhes" onClick={() => openEvent(e)}><Icon name="arrowRight" size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={10}>
                  <EmptyState icon="audit" title="Nenhum evento encontrado" description="Ajuste os filtros ou execute ações administrativas para gerar novos registros." />
                </td></tr>
              )}
            </tbody>
          </table>
        )}

        <div className="pagination">
          <span>{useRealApi ? totalCount : filtered.length} eventos · ordenados por mais recente</span>
          {useRealApi && (
            <div className="pages">
              {Array.from({ length: Math.min(totalPages, 5) }).map((_, idx) => {
                const pageNum = idx + 1;
                return (
                  <button key={pageNum} className={page === pageNum ? 'active' : ''} onClick={() => setPage(pageNum)}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AuditEventDrawerV2 event={selected} open={drawerOpen} onClose={() => setDrawerOpen(false)} relatedEvents={relatedEvents} />

      {activeChip === 'critico' && (
        <div style={{ position: 'sticky', bottom: 16, zIndex: 50, marginTop: 8, display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '10px 18px', background: 'var(--danger)', borderRadius: 24, boxShadow: '0 4px 16px rgba(236,42,88,0.35)', color: 'white', fontSize: 13, fontWeight: 600 }}>
            <Icon name="alert" size={15} style={{ color: 'white' }} />
            Exibindo apenas eventos críticos — {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
            <button
              onClick={() => { setActiveChip('all'); setSeverityF('all'); }}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', border: '1px solid rgba(255,255,255,0.5)', borderRadius: 12, background: 'rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >
              <Icon name="x" size={11} style={{ color: 'white' }} />Limpar filtro
            </button>
          </div>
        </div>
      )}

      {!useRealApi && (
        <RetentionConfigModal
          open={retentionModal}
          onClose={() => setRetentionModal(false)}
          currentPolicy={retentionPolicy}
          onSave={(policy) => {
            setRetentionPolicy(policy);
            setRetentionModal(false);
          }}
        />
      )}
    </div>
  );
};

export {
  AuditoriaV2Page,
  ResultBadge,
  SeverityBadge,
};
